import { Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
} from 'recharts';
import {
  adminRevenuePeriods, adminInventoryPurchases, adminExpenses,
  type RevenuePeriod, type InventoryPurchase, type Expense,
} from '../../api/admin';

type DatePreset = 'all' | 'period';

const PIE_REV_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6'];
const PIE_EXP_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#64748b', '#6b7280'];

const inr = (n: number) => `₹${Math.round(Number.isFinite(n) ? n : 0).toLocaleString('en-IN')}`;
const pct = (n: number) => `${n.toFixed(1)}%`;

function fmtAxisTick(v: number): string {
  const abs = Math.abs(v);
  const neg = v < 0 ? '-' : '';
  if (abs >= 100000) return `₹${neg}${(abs / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `₹${neg}${(abs / 1000).toFixed(0)}k`;
  return `₹${neg}${abs}`;
}


function periodShortLabel(p: RevenuePeriod): string {
  if (!p.period_from) return 'N/A';
  const d1 = new Date(p.period_from);
  const m1 = d1.toLocaleDateString('en-IN', { month: 'short' });
  const y1 = String(d1.getFullYear()).slice(2);
  if (!p.period_to) return `${m1} '${y1}`;
  const d2 = new Date(p.period_to);
  const m2 = d2.toLocaleDateString('en-IN', { month: 'short' });
  const y2 = String(d2.getFullYear()).slice(2);
  if (m1 === m2 && y1 === y2) return `${m1} '${y1}`;
  if (y1 === y2) return `${m1}–${m2} '${y1}`;
  return `${m1} '${y1}–${m2} '${y2}`;
}

const istToday = () => new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);

const dateShort = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—';

interface PeriodPL {
  period: RevenuePeriod;
  label: string;
  onlineSales: number;
  totalRevenue: number;
  purchaseCost: number;
  inhouseCost: number;
  cogs: number;
  grossProfit: number;
  grossMarginPct: number;
  advertising: number;
  packaging: number;
  shippingRTO: number;
  remittanceBank: number;
  otherCharges: number;
  miscExpenses: number;
  opExpenses: number;
  netProfit: number;
  netMarginPct: number;
}

function computePeriodPL(
  period: RevenuePeriod,
  purchases: InventoryPurchase[],
  expenses: Expense[],
): PeriodPL {
  const from = period.period_from?.slice(0, 10) ?? '';
  const to = period.period_to?.slice(0, 10) || istToday();

  const onlineSales = period.total_revenue;
  const totalRevenue = onlineSales;

  const periodPurchases = purchases.filter(p => {
    if (p.status === 'cancelled') return false;
    const d = p.order_date?.slice(0, 10) ?? '';
    if (!d) return false;
    if (from && d < from) return false;
    if (d > to) return false;
    return true;
  });
  const purchaseCost = periodPurchases.reduce((s, p) => s + p.amount, 0);
  const inhouseCost = period.inhouse_cost;
  const cogs = purchaseCost - inhouseCost;
  const grossProfit = totalRevenue - cogs;
  const grossMarginPct = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

  const periodExpenses = expenses.filter(e => {
    const d = e.expense_date?.slice(0, 10) ?? '';
    if (!d) return false;
    if (from && d < from) return false;
    if (d > to) return false;
    return true;
  });
  const miscExpenses = periodExpenses.reduce((s, e) => s + e.amount, 0);

  const advertising = period.advertising_spent;
  const packaging = period.packaging_charges;
  const shippingRTO = period.shipway_shipping_rto;
  const remittanceBank = period.shipway_remittance_charges;
  const otherCharges = period.other_charges;
  const opExpenses = advertising + packaging + shippingRTO + remittanceBank + otherCharges + miscExpenses;
  const netProfit = grossProfit - opExpenses;
  const netMarginPct = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

  return {
    period, label: periodShortLabel(period),
    onlineSales, totalRevenue,
    purchaseCost, inhouseCost, cogs,
    grossProfit, grossMarginPct,
    advertising, packaging, shippingRTO, remittanceBank, otherCharges, miscExpenses, opExpenses,
    netProfit, netMarginPct,
  };
}

function BarTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs text-white">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.fill ?? p.color }}>{p.name}: {inr(p.value)}</p>
      ))}
    </div>
  );
}

function PieTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs text-white">
      <p style={{ color: p.fill }}>{p.name}: {inr(p.value)}</p>
    </div>
  );
}

export default function DashboardTab({ isDark }: { isDark: boolean }) {
  const [periods, setPeriods] = useState<RevenuePeriod[]>([]);
  const [purchases, setPurchases] = useState<InventoryPurchase[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState<string[]>([]);
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const muted = isDark ? 'text-gray-400' : 'text-gray-500';
  const divider = isDark ? 'border-gray-800' : 'border-gray-200';
  const gridStroke = isDark ? '#374151' : '#e5e7eb';
  const tickFill = isDark ? '#9ca3af' : '#6b7280';
  const sectionHeader = `text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`;

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      adminRevenuePeriods.list(),
      adminInventoryPurchases.list(),
      adminExpenses.list(),
    ]).then(([r1, r2, r3]) => {
      const errs: string[] = [];
      if (r1.status === 'fulfilled') setPeriods(r1.value);
      else errs.push('Revenue periods failed to load');
      if (r2.status === 'fulfilled') setPurchases(r2.value);
      else errs.push('Purchases failed to load');
      if (r3.status === 'fulfilled') setExpenses(r3.value);
      else errs.push('Expenses failed to load');
      setLoadErrors(errs);
    }).finally(() => setLoading(false));
  }, []);

  const undatedWarnings = useMemo(() => {
    const msgs: string[] = [];
    const noPurchaseDate = purchases.filter(p => p.status !== 'cancelled' && !p.order_date?.slice(0, 10));
    const noExpenseDate = expenses.filter(e => !e.expense_date?.slice(0, 10));
    if (noPurchaseDate.length > 0) msgs.push(`${noPurchaseDate.length} purchase${noPurchaseDate.length > 1 ? 's' : ''} excluded from P&L (no order date)`);
    if (noExpenseDate.length > 0) msgs.push(`${noExpenseDate.length} expense${noExpenseDate.length > 1 ? 's' : ''} excluded from P&L (no expense date)`);
    const noFromDate = periods.filter(p => !p.period_from?.slice(0, 10));
    for (const p of noFromDate) {
      msgs.push(`Period "${p.period_to ? dateShort(p.period_to) : 'unknown'}" has no start date — all historical purchases/expenses are included in its COGS`);
    }
    return msgs;
  }, [purchases, expenses, periods]);

  const visiblePeriods = useMemo(() => {
    if (datePreset === 'period') return periods.filter(p => p.id === selectedPeriodId);
    return periods;
  }, [periods, datePreset, selectedPeriodId]);

  const periodPLs = useMemo(
    () => visiblePeriods.map(p => computePeriodPL(p, purchases, expenses)),
    [visiblePeriods, purchases, expenses],
  );

  const coverageLabel = useMemo(() => {
    if (visiblePeriods.length === 0) return 'No periods selected';
    const froms = visiblePeriods.map(p => p.period_from?.slice(0, 10) ?? '').filter(Boolean).sort();
    const tos = visiblePeriods.map(p => p.period_to?.slice(0, 10) ?? '').filter(Boolean).sort();
    const earliest = froms[0];
    const latest = tos[tos.length - 1];
    if (!earliest && !latest) return 'All periods';
    if (!earliest) return `Up to ${dateShort(latest)}`;
    if (!latest) return `From ${dateShort(earliest)}`;
    return `${dateShort(earliest)} — ${dateShort(latest)}`;
  }, [visiblePeriods]);

  const agg = useMemo(() => {
    const sum = (key: keyof PeriodPL) =>
      periodPLs.reduce((s, p) => s + (p[key] as number), 0);
    const totalRevenue = sum('totalRevenue');
    const grossProfit = sum('grossProfit');
    const netProfit = sum('netProfit');
    const cogs = sum('cogs');
    const opExpenses = sum('opExpenses');
    const totalOrders = visiblePeriods.reduce((s, p) => s + p.total_orders, 0);
    return {
      totalRevenue, grossProfit, netProfit, cogs, opExpenses, totalOrders,
      grossMarginPct: totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0,
      netMarginPct: totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0,
      onlineSales: sum('onlineSales'),
      purchaseCost: sum('purchaseCost'),
      inhouseCost: sum('inhouseCost'),
      advertising: sum('advertising'),
      packaging: sum('packaging'),
      shippingRTO: sum('shippingRTO'),
      remittanceBank: sum('remittanceBank'),
      otherCharges: sum('otherCharges'),
      miscExpenses: sum('miscExpenses'),
    };
  }, [periodPLs, visiblePeriods]);

  const barData = useMemo(
    () => periodPLs.map(p => ({
      name: p.label,
      'Revenue': Math.round(p.totalRevenue),
      'Gross Profit': Math.round(p.grossProfit),
      'Net Profit': Math.round(p.netProfit),
    })),
    [periodPLs],
  );

  const revPieData = useMemo(() => {
    const codRev = visiblePeriods.reduce((s, p) => s + p.cod_revenue, 0);
    const prepaidRev = visiblePeriods.reduce((s, p) => s + p.prepaid_revenue, 0);
    const porterRev = visiblePeriods.reduce((s, p) => s + p.porter_revenue, 0);
    return [
      { name: 'COD', value: Math.round(codRev) },
      { name: 'Prepaid', value: Math.round(prepaidRev) },
      { name: 'Porter', value: Math.round(porterRev) },
    ].filter(d => d.value > 0);
  }, [visiblePeriods]);

  const expPieData = useMemo(() => [
    { name: 'Advertising', value: Math.round(agg.advertising) },
    { name: 'Packaging', value: Math.round(agg.packaging) },
    { name: 'Shipping & RTO', value: Math.round(agg.shippingRTO) },
    { name: 'Shipway Remittance Charges', value: Math.round(agg.remittanceBank) },
    { name: 'Other Charges', value: Math.round(agg.otherCharges) },
    { name: 'Misc Expenses', value: Math.round(agg.miscExpenses) },
  ].filter(d => d.value > 0), [agg]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <span className="text-gray-400 text-sm">Loading dashboard…</span>
      </div>
    );
  }

  const kpiTiles = [
    {
      title: 'Total Revenue',
      value: inr(agg.totalRevenue),
      sub: '',
      color: 'text-emerald-500',
    },
    {
      title: 'Gross Profit',
      value: inr(agg.grossProfit),
      sub: pct(agg.grossMarginPct) + ' margin',
      color: 'text-blue-500',
    },
    {
      title: 'Net Profit',
      value: inr(agg.netProfit),
      sub: pct(agg.netMarginPct) + ' margin',
      color: agg.netProfit >= 0 ? 'text-emerald-500' : 'text-red-500',
    },
    {
      title: 'Total COGS',
      value: inr(agg.cogs),
      sub: '',
      color: 'text-amber-500',
    },
    {
      title: 'Total Op Expenses',
      value: inr(agg.opExpenses),
      sub: '',
      color: isDark ? 'text-gray-300' : 'text-gray-700',
    },
    {
      title: 'Total Orders',
      value: agg.totalOrders.toLocaleString('en-IN'),
      sub: '',
      color: isDark ? 'text-gray-300' : 'text-gray-700',
    },
  ];

  const plRow = `flex justify-between items-baseline gap-4 py-1 text-sm`;

  return (
    <div className="space-y-6">
      {loadErrors.length > 0 && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-500 space-y-0.5">
          {loadErrors.map((e, i) => <p key={i}>⚠ {e}</p>)}
        </div>
      )}
      {undatedWarnings.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-600 space-y-0.5">
          {undatedWarnings.map((w, i) => <p key={i}>ℹ {w}</p>)}
        </div>
      )}
      {/* Date filter pills */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Period:</span>
        <button
          onClick={() => { setDatePreset('all'); setSelectedPeriodId(''); }}
          className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
            datePreset === 'all'
              ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/20'
              : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'
          }`}
        >
          All time
        </button>
        <select
          value={selectedPeriodId}
          onChange={e => {
            const id = e.target.value;
            setSelectedPeriodId(id);
            setDatePreset(id ? 'period' : 'all');
          }}
          className={`px-2 py-1 rounded-lg border text-xs font-semibold transition-all ${
            datePreset === 'period'
              ? 'bg-emerald-500 text-white border-emerald-500'
              : isDark ? 'bg-gray-900 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-500'
          }`}
        >
          <option value="">Select period…</option>
          {[...periods]
            .sort((a, b) => (b.period_from ?? '').localeCompare(a.period_from ?? ''))
            .map(p => (
              <option key={p.id} value={p.id}>
                {dateShort(p.period_from)} → {dateShort(p.period_to)}
              </option>
            ))}
        </select>
      </div>

      {/* Coverage heading */}
      <div>
        <h2 className="text-xl font-bold">{coverageLabel}</h2>
        <p className={`text-xs mt-0.5 ${muted}`}>
          {visiblePeriods.length} period{visiblePeriods.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {kpiTiles.map(tile => (
          <div key={tile.title} className={`rounded-xl border p-5 ${card}`}>
            <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${muted}`}>{tile.title}</p>
            <p className={`text-2xl font-bold ${tile.color}`}>{tile.value}</p>
            {tile.sub && <p className={`text-xs mt-1 ${muted}`}>{tile.sub}</p>}
          </div>
        ))}
      </div>

      {/* P&L Statement */}
      <div className={`rounded-xl border p-6 ${card}`}>
        <p className={sectionHeader}>Profit &amp; Loss Statement</p>
        <div className="max-w-2xl space-y-0">
          <p className={`text-xs font-semibold uppercase tracking-wider mb-1 mt-1 ${muted}`}>Revenue</p>
          <div className={plRow}>
            <span className={muted}>Online Sales (COD + Prepaid + Porter)</span>
            <span>{inr(agg.onlineSales)}</span>
          </div>
          <div className={`border-t ${divider} my-1`} />
          <div className={`${plRow} font-semibold`}>
            <span>Total Revenue</span>
            <span>{inr(agg.totalRevenue)}</span>
          </div>

          <p className={`text-xs font-semibold uppercase tracking-wider mb-1 mt-4 ${muted}`}>Cost of Goods Sold</p>
          <div className={plRow}>
            <span className={muted}>Inventory Purchases</span>
            <span>{inr(agg.purchaseCost)}</span>
          </div>
          <div className={plRow}>
            <span className={muted}>Inhouse Cost</span>
            <span>{inr(agg.inhouseCost)}</span>
          </div>
          <div className={`border-t ${divider} my-1`} />
          <div className={`${plRow} font-semibold`}>
            <span>Total COGS</span>
            <span>{inr(agg.cogs)}</span>
          </div>

          <div className={`border-t ${divider} mt-4 mb-1`} />
          <div className={`${plRow} font-bold text-base`}>
            <span>Gross Profit</span>
            <span className={agg.grossProfit >= 0 ? 'text-blue-500' : 'text-red-500'}>
              {inr(agg.grossProfit)}{' '}
              <span className="text-xs font-normal">({pct(agg.grossMarginPct)})</span>
            </span>
          </div>
          <div className={`border-t ${divider} mb-1`} />

          <p className={`text-xs font-semibold uppercase tracking-wider mb-1 mt-4 ${muted}`}>Operating Expenses</p>
          <div className={plRow}>
            <span className={muted}>Advertising Spent</span>
            <span>{inr(agg.advertising)}</span>
          </div>
          <div className={plRow}>
            <span className={muted}>Packaging Charges</span>
            <span>{inr(agg.packaging)}</span>
          </div>
          <div className={plRow}>
            <span className={muted}>Shipping &amp; RTO</span>
            <span>{inr(agg.shippingRTO)}</span>
          </div>
          <div className={plRow}>
            <span className={muted}>Shipway Remittance Charges</span>
            <span>{inr(agg.remittanceBank)}</span>
          </div>
          <div className={plRow}>
            <span className={muted}>Other Charges</span>
            <span>{inr(agg.otherCharges)}</span>
          </div>
          <div className={plRow}>
            <span className={muted}>Other Expenses (misc)</span>
            <span>{inr(agg.miscExpenses)}</span>
          </div>
          <div className={`border-t ${divider} my-1`} />
          <div className={`${plRow} font-semibold`}>
            <span>Total Expenses</span>
            <span>{inr(agg.opExpenses)}</span>
          </div>

          <div className={`border-t ${divider} mt-4 mb-1`} />
          <div className={`${plRow} font-bold text-base`}>
            <span>Net Profit / (Loss)</span>
            <span className={agg.netProfit >= 0 ? 'text-emerald-500' : 'text-red-500'}>
              {inr(agg.netProfit)}{' '}
              <span className="text-xs font-normal">({pct(agg.netMarginPct)})</span>
            </span>
          </div>
          <div className={`border-t ${divider}`} />
        </div>
      </div>

      {/* Charts row: bar chart (2/3) + revenue pie (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`lg:col-span-2 rounded-xl border p-5 ${card}`}>
          <p className={sectionHeader}>Revenue vs Profit by Period</p>
          {barData.length === 0 ? (
            <div className={`flex items-center justify-center h-52 text-sm ${muted}`}>No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={barData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis dataKey="name" tick={{ fill: tickFill, fontSize: 11 }} />
                <YAxis tickFormatter={fmtAxisTick} tick={{ fill: tickFill, fontSize: 11 }} width={72} />
                <Tooltip content={<BarTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, color: tickFill }} />
                <Bar dataKey="Revenue" fill="#10b981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Gross Profit" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Net Profit" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={`rounded-xl border p-5 ${card}`}>
          <p className={sectionHeader}>Revenue Breakdown</p>
          {revPieData.length === 0 ? (
            <div className={`flex items-center justify-center h-52 text-sm ${muted}`}>No data</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={revPieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" nameKey="name">
                    {revPieData.map((_, i) => (
                      <Cell key={i} fill={PIE_REV_COLORS[i % PIE_REV_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {revPieData.map((d, i) => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PIE_REV_COLORS[i % PIE_REV_COLORS.length] }} />
                      <span className={muted}>{d.name}</span>
                    </div>
                    <span className="font-medium">{inr(d.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Expense breakdown pie */}
      <div className={`rounded-xl border p-5 ${card}`}>
        <p className={sectionHeader}>Expense Breakdown</p>
        {expPieData.length === 0 ? (
          <div className={`flex items-center justify-center h-48 text-sm ${muted}`}>No expense data</div>
        ) : (
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <div style={{ width: '100%', maxWidth: 320, height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={expPieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" nameKey="name">
                    {expPieData.map((_, i) => (
                      <Cell key={i} fill={PIE_EXP_COLORS[i % PIE_EXP_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2 w-full lg:w-auto">
              {expPieData.map((d, i) => (
                <div key={d.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: PIE_EXP_COLORS[i % PIE_EXP_COLORS.length] }} />
                    <span className={muted}>{d.name}</span>
                  </div>
                  <span className="font-medium">{inr(d.value)}</span>
                </div>
              ))}
              <div className={`border-t pt-2 ${divider} flex justify-between text-sm font-semibold`}>
                <span>Total</span>
                <span>{inr(agg.opExpenses)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Period-wise collapsible table */}
      <div className={`rounded-xl border overflow-x-auto ${card}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-left border-b ${isDark ? 'text-gray-400 border-gray-800' : 'text-gray-500 border-gray-200'}`}>
              <th className="font-medium py-3 px-4">Period</th>
              <th className="font-medium py-3 px-4 text-right">Revenue</th>
              <th className="font-medium py-3 px-4 text-right">COGS</th>
              <th className="font-medium py-3 px-4 text-right">Gross Profit</th>
              <th className="font-medium py-3 px-4 text-right">Op Expenses</th>
              <th className="font-medium py-3 px-4 text-right">Net Profit</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {periodPLs.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-gray-400">
                  {periods.length === 0 ? 'No revenue periods yet' : 'No periods match the current filter'}
                </td>
              </tr>
            )}
            {periodPLs.slice().sort((a, b) => (b.period.period_from ?? '').localeCompare(a.period.period_from ?? '')).map(pl => {
              const open = expandedId === pl.period.id;
              return (
                <Fragment key={pl.period.id}>
                  <tr
                    onClick={() => setExpandedId(cur => cur === pl.period.id ? null : pl.period.id)}
                    className={`border-b cursor-pointer select-none ${isDark ? 'border-gray-800 hover:bg-gray-800/40' : 'border-gray-100 hover:bg-gray-50'}`}
                  >
                    <td className="py-3 px-4 whitespace-nowrap">
                      {dateShort(pl.period.period_from)}{' '}
                      <span className={muted}>→</span>{' '}
                      {dateShort(pl.period.period_to)}
                    </td>
                    <td className="py-3 px-4 text-right">{inr(pl.totalRevenue)}</td>
                    <td className="py-3 px-4 text-right">{inr(pl.cogs)}</td>
                    <td className="py-3 px-4 text-right font-medium text-blue-500">{inr(pl.grossProfit)}</td>
                    <td className="py-3 px-4 text-right">{inr(pl.opExpenses)}</td>
                    <td className={`py-3 px-4 text-right font-semibold ${pl.netProfit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {inr(pl.netProfit)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <ChevronDown size={15} className={`transition-transform duration-200 ${muted} ${open ? 'rotate-180' : ''}`} />
                    </td>
                  </tr>
                  {open && (
                    <tr className={isDark ? 'bg-gray-800/30' : 'bg-gray-50'}>
                      <td colSpan={7} className="px-6 py-5">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                          <div>
                            <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${muted}`}>Revenue</p>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className={muted}>Online Sales (COD + Prepaid + Porter)</span>
                                <span>{inr(pl.onlineSales)}</span>
                              </div>
                              <div className={`border-t ${divider} pt-1 flex justify-between font-semibold`}>
                                <span>Total</span>
                                <span>{inr(pl.totalRevenue)}</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${muted}`}>COGS</p>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className={muted}>Purchases</span>
                                <span>{inr(pl.purchaseCost)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className={muted}>Inhouse</span>
                                <span>{inr(pl.inhouseCost)}</span>
                              </div>
                              <div className={`border-t ${divider} pt-1 flex justify-between font-semibold`}>
                                <span>Total</span>
                                <span>{inr(pl.cogs)}</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${muted}`}>Op Expenses</p>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className={muted}>Advertising</span>
                                <span>{inr(pl.advertising)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className={muted}>Packaging</span>
                                <span>{inr(pl.packaging)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className={muted}>Shipping &amp; RTO</span>
                                <span>{inr(pl.shippingRTO)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className={muted}>Shipway Remittance Charges</span>
                                <span>{inr(pl.remittanceBank)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className={muted}>Other Charges</span>
                                <span>{inr(pl.otherCharges)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className={muted}>Misc Expenses</span>
                                <span>{inr(pl.miscExpenses)}</span>
                              </div>
                              <div className={`border-t ${divider} pt-1 flex justify-between font-semibold`}>
                                <span>Total</span>
                                <span>{inr(pl.opExpenses)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
